import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KioskDevice, KioskDeviceList, Site } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { useToast } from '@up2d8/ui';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CodeDisplay,
  DefinitionList,
  Empty,
  Field,
  IconBtn,
  Input,
  Modal,
  Note,
  PageHead,
  Select,
  Table,
  useAdminToast,
  type BadgeTone,
} from '../admin-kit';
import { LoadError, Loading, useShortWhen } from '../werkvloer';
import { useSession } from '../../shared/session';

const STATUS_TONE: Record<KioskDevice['status'], BadgeTone> = {
  pending: 'attention',
  active: 'ok',
  revoked: 'neutral',
};

/**
 * Kiosken in het beheer (Stap 40, KIO-2; herontworpen in BHR-6, frames W7 en W8).
 *
 * Een tabel met de stand, en per kiosk hernoemen, opnieuw inrichten (een nieuwe code) en intrekken.
 * De code staat één keer in beeld - in de databank staat alleen de hash. Daarom maakt "Code tonen"
 * bij een kiosk die nog wacht een nieuwe code: de vorige kan niemand meer tonen.
 */
export function KiosksAdminPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const adminToast = useAdminToast();
  const when = useShortWhen();
  const [nieuw, setNieuw] = useState(false);
  const [bewerk, setBewerk] = useState<KioskDevice | null>(null);
  const [code, setCode] = useState<KioskDevice | null>(null);
  const [intrek, setIntrek] = useState<KioskDevice | null>(null);

  const list = useQuery({
    queryKey: ['kiosks'],
    queryFn: async (): Promise<KioskDevice[]> => {
      const { data, error } = await api.GET('/api/v1/kiosks', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as KioskDeviceList).items;
    },
  });

  const vernieuw = () => queryClient.invalidateQueries({ queryKey: ['kiosks'] });
  const fout = (error: unknown) =>
    toast.show({
      tone: 'danger',
      title: t('errors.generic'),
      description: problemDetail(error) ?? undefined,
    });

  const opnieuw = useMutation({
    mutationFn: async (kioskId: string) => {
      const { data, error } = await api.PATCH('/api/v1/kiosks/{kioskId}', {
        params: { path: { kioskId } },
        body: { newPairingCode: true } as never,
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as KioskDevice;
    },
    onSuccess: async (kiosk) => {
      setCode(kiosk);
      await vernieuw();
    },
    onError: fout,
  });

  const intrekken = useMutation({
    mutationFn: async (kioskId: string) => {
      const { error } = await api.DELETE('/api/v1/kiosks/{kioskId}', {
        params: { path: { kioskId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setIntrek(null);
      await vernieuw();
      adminToast.show(t('kiosksAdmin.revoked'));
    },
    onError: fout,
  });

  return (
    <AdminPage>
      <PageHead
        title={t('kiosksAdmin.title')}
        sub={t('beheer.werkvloer.kiosks.sub')}
        actions={
          <Btn icon={<AdminIcon name="plus" />} onClick={() => setNieuw(true)} testId="kiosk-new">
            {t('kiosksAdmin.new')}
          </Btn>
        }
      />

      <Card>
        {list.isPending ? (
          <Loading />
        ) : list.isError ? (
          <LoadError onRetry={() => void list.refetch()} error={list.error} />
        ) : (list.data ?? []).length === 0 ? (
          <Empty
            icon={<AdminIcon name="tablet" size={24} />}
            title={t('kiosksAdmin.emptyTitle')}
            text={t('kiosksAdmin.emptyHint')}
          />
        ) : (
          <Table label={t('kiosksAdmin.title')}>
            <thead>
              <tr>
                <th>{t('kiosksAdmin.name')}</th>
                <th>{t('kiosksAdmin.site')}</th>
                <th>{t('kiosksAdmin.status')}</th>
                <th>{t('kiosksAdmin.lastSeen')}</th>
                <th className="act">
                  <span className="a-sr">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((kiosk) => (
                <tr key={kiosk.id} data-testid="kiosk-row">
                  <td>
                    <span className="a-name">{kiosk.name}</span>
                  </td>
                  <td>{kiosk.siteName ?? '—'}</td>
                  <td>
                    <Badge tone={STATUS_TONE[kiosk.status]}>
                      {t(`beheer.werkvloer.kiosks.status.${kiosk.status}`)}
                    </Badge>
                  </td>
                  <td>{kiosk.lastSeenAt ? when(kiosk.lastSeenAt) : '—'}</td>
                  <ActionsCell>
                    <IconBtn
                      label={t('common.edit')}
                      icon={<AdminIcon name="pencil" />}
                      onClick={() => setBewerk(kiosk)}
                      testId="kiosk-edit"
                    />
                    {kiosk.status === 'pending' ? (
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={<AdminIcon name="qr" />}
                        disabled={opnieuw.isPending}
                        onClick={() => opnieuw.mutate(kiosk.id)}
                        testId="kiosk-new-code"
                      >
                        {t('beheer.werkvloer.kiosks.showCode')}
                      </Btn>
                    ) : (
                      <Btn
                        variant="ghost"
                        size="sm"
                        disabled={opnieuw.isPending}
                        onClick={() => opnieuw.mutate(kiosk.id)}
                        testId="kiosk-new-code"
                      >
                        {t('kiosksAdmin.newCode')}
                      </Btn>
                    )}
                    {kiosk.status !== 'revoked' ? (
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => setIntrek(kiosk)}
                        testId="kiosk-revoke"
                      >
                        {t('kiosksAdmin.revoke')}
                      </Btn>
                    ) : null}
                  </ActionsCell>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {nieuw || bewerk ? (
        <KioskModal
          kiosk={bewerk}
          onClose={() => {
            setNieuw(false);
            setBewerk(null);
          }}
          onSaved={async (kiosk) => {
            setNieuw(false);
            setBewerk(null);
            if (kiosk.pairingCode) setCode(kiosk);
            else adminToast.show(t('common.saved'));
            await vernieuw();
          }}
        />
      ) : null}

      {code ? <CodeModal kiosk={code} onClose={() => setCode(null)} /> : null}

      <Modal
        open={intrek !== null}
        onClose={() => setIntrek(null)}
        title={t('beheer.werkvloer.kiosks.revokeTitle', { name: intrek?.name ?? '' })}
        footer={
          <>
            <Btn variant="sec" onClick={() => setIntrek(null)}>
              {t('common.cancel')}
            </Btn>
            <Btn
              variant="danger"
              disabled={intrekken.isPending}
              onClick={() => intrek && intrekken.mutate(intrek.id)}
              testId="kiosk-revoke-confirm"
            >
              {t('kiosksAdmin.revoke')}
            </Btn>
          </>
        }
      >
        <Note tone="danger">{t('kiosksAdmin.revokeConfirm')}</Note>
      </Modal>
      {adminToast.element}
    </AdminPage>
  );
}

/**
 * De koppelcode (W8): groot, met hoe lang ze geldig is en het adres om op de tablet te openen.
 * Beide komen uit de echte gegevens: de vervaldatum van de server, het adres van dit portaal.
 */
function CodeModal({ kiosk, onClose }: { kiosk: KioskDevice; onClose: () => void }) {
  const { t } = useTranslation();
  const when = useShortWhen();
  const address = `${window.location.host}/kiosk`;
  return (
    <Modal
      open
      onClose={onClose}
      title={kiosk.siteName ? `${kiosk.name} · ${kiosk.siteName}` : kiosk.name}
      testId="kiosk-code-dialog"
      footer={<Btn onClick={onClose}>{t('beheer.werkvloer.done')}</Btn>}
    >
      <p>{t('beheer.werkvloer.kiosks.codeHint')}</p>
      <CodeDisplay
        code={kiosk.pairingCode ?? ''}
        label={t('beheer.werkvloer.kiosks.codeLabel')}
        warning={t('kiosksAdmin.codeOnce')}
        testId="kiosk-code"
      />
      <DefinitionList
        items={[
          ...(kiosk.pairingExpiresAt
            ? ([[t('beheer.werkvloer.kiosks.validUntil'), when(kiosk.pairingExpiresAt)]] as [
                string,
                string,
              ][])
            : []),
          [t('beheer.werkvloer.kiosks.onTablet'), <span className="a-mono">{address}</span>],
        ]}
      />
    </Modal>
  );
}

/** Nieuwe kiosk of kiosk wijzigen: naam en vestiging, een korte vorm in een venster. */
function KioskModal({
  kiosk,
  onClose,
  onSaved,
}: {
  kiosk: KioskDevice | null;
  onClose: () => void;
  onSaved: (kiosk: KioskDevice) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const [name, setName] = useState(kiosk?.name ?? '');
  const [siteId, setSiteId] = useState(kiosk?.siteId ?? '');

  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: async (): Promise<Site[]> => {
      const { data } = await api.GET('/api/v1/sites', {});
      return (data as { items: Site[] } | undefined)?.items ?? [];
    },
  });

  const bewaar = useMutation({
    mutationFn: async (): Promise<KioskDevice> => {
      if (kiosk) {
        const { data, error } = await api.PATCH('/api/v1/kiosks/{kioskId}', {
          params: { path: { kioskId: kiosk.id } },
          body: { name: name.trim(), siteId: siteId || null } as never,
        });
        if (error || !data) throw error ?? new Error('Mislukt');
        return data as KioskDevice;
      }
      const { data, error } = await api.POST('/api/v1/kiosks', {
        body: { name: name.trim(), siteId: siteId || null } as never,
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as KioskDevice;
    },
    onSuccess: (saved) => void onSaved(saved),
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: t('errors.generic'),
        description: problemDetail(error) ?? undefined,
      }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={kiosk ? t('kiosksAdmin.edit') : t('kiosksAdmin.new')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            disabled={name.trim().length < 2 || bewaar.isPending}
            onClick={() => bewaar.mutate()}
            testId="kiosk-save"
          >
            {kiosk ? t('common.save') : t('beheer.werkvloer.kiosks.create')}
          </Btn>
        </>
      }
    >
      <Field label={t('kiosksAdmin.name')}>
        {(props) => (
          <Input
            {...props}
            placeholder={t('kiosksAdmin.namePlaceholder')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            data-testid="kiosk-name"
          />
        )}
      </Field>
      {(sites.data ?? []).length > 0 ? (
        <Field label={t('kiosksAdmin.site')}>
          {(props) => (
            <Select {...props} value={siteId} onChange={(event) => setSiteId(event.target.value)}>
              <option value="">—</option>
              {(sites.data ?? []).map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      ) : null}
    </Modal>
  );
}
